// Kennel schedule model + pure occurrence math (Stories 3.4/3.5). Recurrence is
// date-only here; the time + IANA zone attach to each generated trail. No I/O.
function ymd(d) {
    return d.toISOString().slice(0, 10);
}
function parse(date) {
    const [y, m, dd] = date.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, dd));
}
/** Inclusive count of months added to a YYYY-MM-DD, clamping day overflow. */
export function addMonths(date, months) {
    const d = parse(date);
    const target = d.getUTCMonth() + months;
    const year = d.getUTCFullYear() + Math.floor(target / 12);
    const month = ((target % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return ymd(new Date(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDay))));
}
/**
 * All occurrence dates (YYYY-MM-DD) for a recurrence in [from, to] inclusive.
 * `from`/`to` are date strings; computation is in UTC and tz-agnostic (the day
 * a weekly/monthly rule lands on doesn't depend on the trail's clock zone).
 */
export function scheduleOccurrences(recurrence, from, to) {
    const start = parse(from);
    const end = parse(to);
    const out = [];
    if (recurrence.freq === "weekly" || recurrence.freq === "biweekly") {
        const step = recurrence.freq === "biweekly" ? 14 : 7;
        const d = new Date(start);
        // Advance to the first matching weekday on/after `from`.
        const delta = (recurrence.weekday - d.getUTCDay() + 7) % 7;
        d.setUTCDate(d.getUTCDate() + delta);
        // Biweekly's phase must not depend on `from` (generation restarts at today
        // on every horizon extend), so align to a fixed 14-day grid: keep only the
        // matching weekday whose whole-week index from the epoch is even, then skip
        // a week if the first candidate lands on an odd week.
        if (recurrence.freq === "biweekly") {
            const weekIndex = Math.floor(d.getTime() / 86_400_000 / 7);
            if (weekIndex % 2 !== 0)
                d.setUTCDate(d.getUTCDate() + 7);
        }
        for (; d <= end; d.setUTCDate(d.getUTCDate() + step))
            out.push(ymd(d));
        return out;
    }
    // Lunar: one occurrence per month — the chosen weekday nearest that month's
    // full/new moon. Successive months' moons are ~29.5 days apart and the
    // weekday snap moves a date by at most 3.5 days, so the results stay strictly
    // ascending (gaps ≥ ~22 days); the `includes` guard only defends the range
    // boundary, where a snapped date can fall into a neighbouring month.
    if (recurrence.freq === "lunar") {
        const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
        while (cursor <= end) {
            const moon = lunarPhaseInstant(recurrence.phase, cursor.getUTCFullYear(), cursor.getUTCMonth());
            // A month with no full/new moon (rare, e.g. a moonless February) yields
            // no trail; otherwise snap to the nearest matching weekday.
            if (moon) {
                const occ = nearestWeekday(moon, recurrence.weekday);
                const s = ymd(occ);
                if (occ >= start && occ <= end && !out.includes(s))
                    out.push(s);
            }
            cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        }
        return out;
    }
    // Monthly: the Nth (or last) given weekday of each month in range.
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    while (cursor <= end) {
        const occ = nthWeekdayOfMonth(cursor.getUTCFullYear(), cursor.getUTCMonth(), recurrence.nth, recurrence.weekday);
        if (occ && occ >= start && occ <= end)
            out.push(ymd(occ));
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return out;
}
/**
 * The UTC date (00:00) of the target `weekday` (0=Sun…6=Sat) nearest `instant`.
 * Distance is measured from each candidate weekday's midnight to the instant, so
 * a moon falling exactly between two same-weekdays (3.5 days from each) is a tie;
 * ties resolve to the earlier date.
 */
export function nearestWeekday(instant, weekday) {
    const day = Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
    // Most recent target weekday on/before the instant's calendar day.
    const back = (new Date(day).getUTCDay() - weekday + 7) % 7;
    const prev = day - back * 86_400_000;
    const next = prev + 7 * 86_400_000;
    const t = instant.getTime();
    // `<` (not `<=`) keeps the earlier date on an exact tie.
    return new Date(Math.abs(next - t) < Math.abs(prev - t) ? next : prev);
}
// --- Lunar phase math (Jean Meeus, *Astronomical Algorithms*, ch. 49) ---------
// Pure and self-contained — no library or I/O. We need only day-level accuracy
// (to pick the nearest weekday), so the small planetary ("A") corrections and
// the TD→UT ΔT offset (tens of seconds) are intentionally omitted; the moon
// instant is treated as UTC. Results are good to a couple of minutes, which is
// far finer than the surrounding date arithmetic requires.
const DEG = Math.PI / 180;
/** Julian Ephemeris Day of the new/full moon for lunation index `k`. */
function lunarPhaseJDE(k, phase) {
    const T = k / 1236.85;
    const T2 = T * T;
    const T3 = T2 * T;
    const T4 = T3 * T;
    let jde = 2451550.09766 +
        29.530588861 * k +
        0.00015437 * T2 -
        0.00000015 * T3 +
        0.00000000073 * T4;
    const E = 1 - 0.002516 * T - 0.0000074 * T2;
    const M = (2.5534 + 29.1053567 * k - 0.0000014 * T2 - 0.00000011 * T3) * DEG;
    const Mp = (201.5643 +
        385.81693528 * k +
        0.0107582 * T2 +
        0.00001238 * T3 -
        0.000000058 * T4) *
        DEG;
    const F = (160.7108 +
        390.67050284 * k -
        0.0016118 * T2 -
        0.00000227 * T3 +
        0.000000011 * T4) *
        DEG;
    const Om = (124.7746 - 1.56375588 * k + 0.0020672 * T2 + 0.00000215 * T3) * DEG;
    const s = Math.sin;
    // The first seven periodic terms differ between new and full moon.
    if (phase === "new") {
        jde +=
            -0.4072 * s(Mp) +
                0.17241 * E * s(M) +
                0.01608 * s(2 * Mp) +
                0.01039 * s(2 * F) +
                0.00739 * E * s(Mp - M) -
                0.00514 * E * s(Mp + M) +
                0.00208 * E * E * s(2 * M);
    }
    else {
        jde +=
            -0.40614 * s(Mp) +
                0.17302 * E * s(M) +
                0.01614 * s(2 * Mp) +
                0.01043 * s(2 * F) +
                0.00734 * E * s(Mp - M) -
                0.00515 * E * s(Mp + M) +
                0.00209 * E * E * s(2 * M);
    }
    // The remaining terms are common to both phases.
    jde +=
        -0.00111 * s(Mp - 2 * F) -
            0.00057 * s(Mp + 2 * F) +
            0.00056 * E * s(2 * Mp + M) -
            0.00042 * s(3 * Mp) +
            0.00042 * E * s(M + 2 * F) +
            0.00038 * E * s(M - 2 * F) -
            0.00024 * E * s(2 * Mp - M) -
            0.00017 * s(Om) -
            0.00007 * s(Mp + 2 * M) +
            0.00004 * s(2 * Mp - 2 * F) +
            0.00004 * s(3 * M) +
            0.00003 * s(Mp + M - 2 * F) +
            0.00003 * s(2 * Mp + 2 * F) -
            0.00003 * s(Mp + M + 2 * F) +
            0.00003 * s(Mp - M + 2 * F) -
            0.00002 * s(Mp - M - 2 * F) -
            0.00002 * s(3 * Mp + M) +
            0.00002 * s(4 * Mp);
    return jde;
}
/** Julian Day → JS Date (JD 2440587.5 = 1970-01-01T00:00:00Z). */
function jdToDate(jd) {
    return new Date((jd - 2440587.5) * 86_400_000);
}
/**
 * The instant (UTC) of the full/new moon that falls within the given calendar
 * month (`monthIndex` 0=Jan…11=Dec), or null if the month has none. We use the
 * in-month phase rather than the one merely nearest the 15th, so a month always
 * gets *its own* moon — e.g. June takes its late-June full moon even when the
 * prior month's moon sits a touch closer to June 15th. In a "blue moon" month
 * (two of the same phase), the earlier one wins.
 */
export function lunarPhaseInstant(phase, year, monthIndex) {
    // ~12.3685 lunations per year since the 2000 epoch; full moons sit at k+0.5.
    const kApprox = (year + (monthIndex + 0.5) / 12 - 2000) * 12.3685;
    const base = phase === "new" ? Math.round(kApprox) : Math.round(kApprox - 0.5) + 0.5;
    // The in-month phase's lunation index is within ±1 of the mid-month estimate;
    // probing the bracketing lunations and keeping the earliest one that lands in
    // the month covers both the normal and double-moon cases.
    let earliest = null;
    for (const k of [base - 1, base, base + 1]) {
        const d = jdToDate(lunarPhaseJDE(k, phase));
        if (d.getUTCFullYear() === year && d.getUTCMonth() === monthIndex) {
            if (!earliest || d.getTime() < earliest.getTime())
                earliest = d;
        }
    }
    return earliest;
}
function nthWeekdayOfMonth(year, month, nth, weekday) {
    if (nth === -1) {
        const last = new Date(Date.UTC(year, month + 1, 0));
        const back = (last.getUTCDay() - weekday + 7) % 7;
        last.setUTCDate(last.getUTCDate() - back);
        return last;
    }
    const first = new Date(Date.UTC(year, month, 1));
    const forward = (weekday - first.getUTCDay() + 7) % 7;
    const day = 1 + forward + (nth - 1) * 7;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    if (day > lastDay)
        return null; // e.g. no 5th Tuesday
    return new Date(Date.UTC(year, month, day));
}
